import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext
} from 'react';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';
import { useAudio } from 'features/audio/audio';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { autoplayNextEvent } from 'features/events/thunks';
import { setAutoplay } from 'features/events/eventsSlice';
import { AutoplayCountdown } from 'features/events/components/AutoplayCountdown';
import styles from '../styles.module.scss';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { IconButton } from 'components/Buttons/IconButton';

const millsecondsUntilAutoplay = 20000;

interface AutoplayTimerProps {
  enabled: boolean;
}

export const AutoplayTimer: FunctionComponent<AutoplayTimerProps> = (
  props: AutoplayTimerProps
) => {
  const [timerId, setTimerId] = useState<number | null>(null);
  const [timeStarted, setTimeStarted] = useState<DateTime | null>(null);

  const dispatch = useDispatch();
  const { loadAudio } = useAudio();
  const navigate = useNavigate();
  const { isCreatorAppWebview, isListenerAppWebview, isEmbed } =
    useContext(UserAgentContext);
  const { pathname } = useLocation();
  const isEventPath = matchPath('/events/:id', pathname);

  useEffect(() => {
    if (props.enabled === true && !timerId) {
      setTimerId(
        window.setTimeout(() => {
          dispatch(
            autoplayNextEvent(
              loadAudio,
              navigate,
              isListenerAppWebview,
              isEmbed
            )
          );
        }, millsecondsUntilAutoplay)
      );
      setTimeStarted(DateTime.now());
    }
    if (props.enabled === false && timerId) {
      window.clearTimeout(timerId);
      setTimerId(null);
      setTimeStarted(null);
    }
    return () => {
      if (timerId) {
        window.clearTimeout(timerId);
        setTimerId(null);
        setTimeStarted(null);
      }
    };
  }, [
    props.enabled,
    dispatch,
    loadAudio,
    navigate,
    timerId,
    isEmbed,
    isListenerAppWebview
  ]);

  if (props.enabled === false || !timeStarted) {
    return null;
  }

  if (isListenerAppWebview && !isEventPath) {
    return null;
  }

  if (isCreatorAppWebview) {
    return null;
  }

  const cancelAutoplay = () => {
    dispatch(setAutoplay(false));
  };

  return (
    <div className={styles['auto-play']}>
      <AutoplayCountdown
        startedAt={timeStarted}
        millisecondsUntilAutoplay={millsecondsUntilAutoplay}
      />
      <span className={styles['auto-play__text']}>Autoplaying next event</span>
      <IconButton
        showLabel={false}
        icon={'x'}
        label={'Cancel auto play'}
        onClick={cancelAutoplay}
        iconSize={12}
        size={'small'}
      />
    </div>
  );
};
